import { saveStateToStorage } from '@sky-distribution/gp-onboarding-shared-utils';
import { useSelector } from '@xstate/react';
import { useCallback, useContext } from 'react';
import { DeepPartial } from 'react-hook-form';
import { NON_PERSISTENT_STATE } from '../const';
import { NextData, QuoteData } from '../events';
import { AppStateContext } from '../provider/AppStateProvider';
import {
  canGoBackSelector,
  canGoToWelcomeSelector,
  contextSelector,
  isBusySelector,
  isDraftSelector,
  isErrorSelector,
  isInvalidOtpSelector,
  isInvalidPhoneNumberSelector,
  isLoadingSelector,
  isMfEnabledSelector,
  isSpinnerLoadingSelector,
  metaSelector,
} from '../selectors';
import { UseAppState } from './useAppState.types';

export const useAppState = (): UseAppState => {
  const stateMachineService = useContext(AppStateContext);

  const context = useSelector(stateMachineService, contextSelector);
  const meta = useSelector(stateMachineService, metaSelector);
  const canGoBack = useSelector(stateMachineService, canGoBackSelector);
  const canGoWelcome = useSelector(stateMachineService, canGoToWelcomeSelector);
  const isError = useSelector(stateMachineService, isErrorSelector);
  const isLoading = useSelector(stateMachineService, isLoadingSelector);
  const isSpinnerLoading = useSelector(stateMachineService, isSpinnerLoadingSelector);
  const isBusy = useSelector(stateMachineService, isBusySelector);
  const isDraft = useSelector(stateMachineService, isDraftSelector);
  const isInvalidOtp = useSelector(stateMachineService, isInvalidOtpSelector);
  const isInvalidPhoneNumber = useSelector(stateMachineService, isInvalidPhoneNumberSelector);
  const useMfEnabled = (mf: string) => useSelector(stateMachineService, isMfEnabledSelector(mf));

  const throwError = useCallback((): void => {
    stateMachineService.send({ type: 'ERROR' });
  }, [stateMachineService]);

  const goToWelcome = useCallback((): void => {
    stateMachineService.send({ type: 'GO_WELCOME' });
  }, [stateMachineService]);

  const previous = useCallback((): void => {
    stateMachineService.send({ type: 'PREVIOUS' });
  }, [stateMachineService]);

  const saveData = useCallback(
    (data: DeepPartial<NextData>): void => {
      stateMachineService.send({
        type: 'SAVE_DATA',
        data,
      });
    },
    [stateMachineService],
  );

  const next = useCallback(
    (data: NextData): void => {
      stateMachineService.send({
        type: 'NEXT',
        data,
      });
    },
    [stateMachineService],
  );

  const quote = useCallback(
    (data: QuoteData): void => {
      stateMachineService.send({
        type: 'QUOTE',
        data,
      });
    },
    [stateMachineService],
  );

  const markChangePending = useCallback((): void => {
    stateMachineService.send({ type: 'CHANGE_PENDING' });
  }, [stateMachineService]);

  const draft = useCallback((): void => {
    stateMachineService.send({ type: 'POLICY_DRAFT' });
  }, [stateMachineService]);

  stateMachineService.onTransition((state) => {
    if (state.changed && !state.hasTag(NON_PERSISTENT_STATE)) {
      saveStateToStorage(state, context.route);
    }
  });

  return {
    useMfEnabled,
    context,
    meta,
    canGoBack,
    canGoWelcome,
    isLoading,
    isSpinnerLoading,
    isBusy,
    isError,
    isDraft,
    isInvalidOtp,
    isInvalidPhoneNumber,
    throwError,
    previous,
    goToWelcome,
    next,
    quote,
    markChangePending,
    draft,
    saveData,
  };
};
